import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);

      // Update active section based on scroll position
      const sections = ['home', 'about', 'how-it-works', 'offerings', 'benefits', 'research', 'news', 'contact'];
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150 && rect.bottom >= 150) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setIsMenuOpen(false); // Close mobile menu on click
    }
  };

  const navItems = [
    { id: 'about', label: 'About' },
    { id: 'how-it-works', label: 'How It Works' },
    { id: 'offerings', label: 'Offerings' },
    { id: 'benefits', label: 'Benefits' },
    { id: 'research', label: 'Research' },
    { id: 'news', label: 'News' },
    { id: 'contact', label: 'Contact' }
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled || isMenuOpen
        ? 'bg-[#F6F6F6]/95 backdrop-blur-md shadow-lg py-4'
        : 'bg-transparent py-8'
        }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <div className="flex items-center space-x-4 cursor-pointer" onClick={() => scrollToSection('home')}>
          <div className="w-14 h-14 rounded-full overflow-hidden shadow-lg border-2 border-white/20 bg-white">
            <img
              src="/logo.png"
              alt="Sage And Seed Logo"
              className="w-full h-full object-contain p-1"
            />
          </div>
          <span className="text-2xl font-bold tracking-tight" style={{
            fontFamily: 'Merriweather, serif',
            color: scrolled || isMenuOpen ? '#2c3e50' : '#ffffff',
            textShadow: scrolled || isMenuOpen ? 'none' : '0 2px 8px rgba(0,0,0,0.4)'
          }}>
            Sage And Seed
          </span>
        </div>

        {/* Desktop Menu */}
        <div className="hidden md:flex items-center space-x-10">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className={`text-base font-semibold transition-all duration-300 relative ${activeSection === item.id
                ? (scrolled || isMenuOpen ? 'text-[#2c3e50]' : 'text-white')
                : (scrolled || isMenuOpen ? 'text-[#5a6c7d] hover:text-[#2c3e50]' : 'text-white/90 hover:text-white')
                }`}
              style={{
                textShadow: scrolled || isMenuOpen ? 'none' : '0 1px 4px rgba(0,0,0,0.5)'
              }}
            >
              {item.label}
              {activeSection === item.id && (
                <span
                  className="absolute -bottom-1 left-0 right-0 h-0.5 rounded-full"
                  style={{ background: 'linear-gradient(90deg, #FFE5D9 0%, #C8D5B9 100%)' }}
                ></span>
              )}
            </button>
          ))}
        </div>

        <div className="flex items-center space-x-4">
          <Button
            onClick={() => window.location.href = 'https://helping-buddy-fe-9o6r.vercel.app/'}
            className="hidden md:block rounded-full px-6 py-2 font-medium transition-all duration-300"
            style={{
              background: 'linear-gradient(135deg, #1E56A0 0%, #163172 100%)',
              color: '#F6F6F6',
              border: 'none'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(30, 86, 160, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            Get Started
          </Button>
        </div>


        {/* Mobile Menu Toggle */}
        <button
          className={`md:hidden ${scrolled || isMenuOpen ? 'text-[#163172]' : 'text-white'}`}
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </button>
      </div>

      {/* Mobile Sidebar */}
      <div
        className={`fixed inset-y-0 right-0 z-40 w-64 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out md:hidden ${isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        style={{
          marginTop: "64px", // height of the navbar approximately
          height: "calc(100vh - 64px)",
          borderLeft: "1px solid rgba(0,0,0,0.05)"
        }}
      >
        <div className="flex flex-col p-6 space-y-6 h-full overflow-y-auto">
          {navItems.map((item) => (
            <button
              key={item.id}
              onClick={() => scrollToSection(item.id)}
              className={`text-lg text-left font-medium transition-all duration-300 ${activeSection === item.id ? 'text-[#2c3e50] font-bold' : 'text-[#5a6c7d]'
                }`}
            >
              {item.label}
            </button>
          ))}
          <Button
            onClick={() => window.location.href = 'https://helping-buddy-fe-9o6r.vercel.app/'}
            className="w-full rounded-full px-6 py-3 font-medium transition-all duration-300 mt-2"
            style={{
              background: 'linear-gradient(135deg, #FFE5D9 0%, #F5D5C8 100%)',
              color: '#2c3e50',
              border: 'none'
            }}
          >
            Get Started
          </Button>
        </div>
      </div>

      {/* Overlay background for mobile menu */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-30 md:hidden"
          onClick={() => setIsMenuOpen(false)}
          style={{ top: "64px" }}
        />
      )}
    </nav>
  );
};

export default Navigation;
